import { Handle, Position } from "@xyflow/react";
import LockIcon from '@mui/icons-material/Lock';
import { Box, Typography, useTheme } from "@mui/material";
import ReusableNodes, { NodeContent, NodeError, NodeHandles, NodeHeader, NodeWarning } from "../../../../../components/ReusableNodes";
import { CustomTooltip } from "../../../../../components/TooltipCustomStyle";
import TooltipStatusTitle from "../../../../../components/TooltipStatusTitle";
import ErrorIcon from '@mui/icons-material/Error';
import { mask } from "../../../../../utils/privacy";
import isSelectedAndHighlight from "../../../../../utils/isSelectedAndHighlight";
// import DrgIcon from "../../drgicons/DrgIcon";

export default function CustomPrivacyNode({ data, selected, id, onNodeClick }) {

    const theme = useTheme();

    const label = data.general_properties.label;
    const description = data.general_properties.description;

    const handleNodeClick = (event) => {
        event.stopPropagation(); // ป้องกัน event bubbling
        if (onNodeClick) {
            onNodeClick(event, id);
        }
    };

    const nodeContent = () => {
        const privacies = data?.general_properties?.privacies || [];
        const displayMethod = (method) => {
            switch (method) {
                case 'masking':
                    return 'Masking';
                case 'encrypt':
                    return 'Encrypt';
                default:
                    return method;
            }
        };

        return privacies.map((privacy, index) => (
            <div key={index} className={`whitespace-nowrap border border-gray-300 p-2 rounded-md leading-4 ${index > 0 ? 'mt-2' : ''}`}>
                <span className="font-medium text-gray-700 text-sm">{displayMethod(privacy.method)}</span>{' - '}
                <span className="font-medium text-blue-700 text-sm">{privacy.attribute?.name}</span>
                {(privacy.method === 'masking') && <div className="font-normal text-gray-700 text-sm">Exp. {mask("Preview Message", Number(privacy?.visible_length), privacy?.symbol, privacy?.position)}</div>}
            </div>
        ));
    };

    const nodeHandles = ({ headerAnchorY } = {}) => {
        const handleClassName = `drg-handle ${selected ? 'drg-selected-handle' : ''}`;

        const sideHandleStyle = typeof headerAnchorY === 'number'
            ? { top: `${headerAnchorY}px` }
            : undefined;

        return (
            <div>
                <Handle
                    id="top-target"
                    type="target"
                    position={Position.Top}
                    className={handleClassName}
                />
                <Handle
                    id="top-source"
                    type="source"
                    position={Position.Top}
                    className={handleClassName}
                />
                <Handle
                    id="bottom-target"
                    type="target"
                    position={Position.Bottom}
                    className={handleClassName}
                />
                <Handle
                    id="bottom-source"
                    type="source"
                    position={Position.Bottom}
                    className={handleClassName}
                />
                <Handle
                    id="left-target"
                    type="target"
                    position={Position.Left}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="left-source"
                    type="source"
                    position={Position.Left}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="right-target"
                    type="target"
                    position={Position.Right}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="right-source"
                    type="source"
                    position={Position.Right}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
            </div>
        )
    };

    return (
        <ReusableNodes
            // selected={isSelectedAndHighlight(selected, highlighted)}
            selected={selected}
            highlighted={data?.highlighted}
            focusMode={data?.focusMode}
            backgroundColor="#ffffff"
            onClick={handleNodeClick}
        >
            {data?.warning === true && (
                <NodeWarning>
                    <CustomTooltip title={<TooltipStatusTitle headerTitle="Warning" listDetails={data?.warning_text} />} arrow placement="top" tooltipcolorstyle={theme.palette.warning.main}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: '#fff',
                                width: 15,
                                height: 15,
                                marginTop: '5px'
                            }}
                        >
                            <ErrorIcon sx={{
                                color: theme.palette.warning.main,
                                fontSize: 18,
                            }} />
                        </Box>
                    </CustomTooltip>
                </NodeWarning>
            )}
            {(data?.error === true || data?.error_text.length > 0) && (
                <NodeError>
                    <CustomTooltip title={<TooltipStatusTitle headerTitle="Error" listDetails={data?.error_text} />} arrow placement="top" tooltipcolorstyle={theme.palette.error.main}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: '#fff',
                                width: 15,
                                height: 15,
                                marginTop: '5px'
                            }}
                        >
                            <ErrorIcon sx={{
                                color: theme.palette.error.main,
                                fontSize: 18,
                            }} />
                        </Box>
                    </CustomTooltip>
                </NodeError>
            )}
            <NodeHeader>
                <div className="drg-icon bg-gray-100">
                    <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        fill="none"
                        preserveAspectRatio="xMidYMid meet"
                        className="block h-6 w-6"
                    >
                        <path d="M7.925 11.185V7.925C7.925 6.844245 8.354326 5.807752 9.118543 5.043543C9.882752 4.279326 10.919245 3.85 12 3.85C13.080771 3.85 14.117288 4.279326 14.881433 5.043543C15.645658 5.807752 16.075 6.844245 16.075 7.925V11.185M6.295 11.185H17.705C18.605249 11.185 19.335 11.914751 19.335 12.815V18.52C19.335 19.420249 18.605249 20.15 17.705 20.15H6.295C5.394775 20.15 4.665 19.420249 4.665 18.52V12.815C4.665 11.914751 5.394775 11.185 6.295 11.185Z" stroke="#3B67E2" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className="drg-label">
                    <Typography variant="body2" className="text-gray-900" sx={{ fontWeight: 700 }}>
                        {label}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: 14 }}>
                        {description}
                    </Typography>
                </div>
            </NodeHeader>
            <NodeContent>
                {nodeContent()}
            </NodeContent>
            <NodeHandles>
                {nodeHandles}
            </NodeHandles>
        </ReusableNodes>
    );
}